import { SegmentDto } from "arps_wasm";
import {
  ArpFields,
  ArpsDesignerKind,
  DurationUnit,
  TypeWellInputTableMode
} from "constants/forecasts.constants";
import { EPSILON, roundTo } from "utils/numbers";

import { ITypeWellProductSettings } from "api/typeWell";

import { ArpSegment } from "models/UserArpsModel";

import { CumulativeData } from "components/forecasting/Forecasting";

import { ICheckedForecast } from "../../../store/features/app/appSlice";
import {
  getDefaultCumUnits,
  getDefaultDeclineUnits,
  getDefaultRateUnits
} from "../../../utils";
import { isRatio } from "../../../utils/arps/productHelpers";
import { ProductTypeEnum } from "../models/ArpsSegment";
import { AVG_DAYS_IN_YEAR, avgMonthsDiff, getDurationInMonths } from "./dates";
import { getDeclineTitleWithType } from "./declineHelpers";
import { calculateRestrictedEUR } from "./restrictedEUR";
import { arpsWasm } from "./wasmLoader";

export function ArpsSegmentToSegmentDto(
  arpsWasm: typeof import("wasm/arps"),
  segment: ArpSegment[]
): SegmentDto[] {
  if (!segment) {
    return [];
  }
  return segment.map((s) => arpsWasm.arpSegmentToSegmentDto(s));
}

export const declineTypes = {
  Secant: "sec",
  Tangent: "tan",
  Nominal: "nom"
};

export function convertNominalToDeclineType({
  nominalDeclineValueDecimal,
  bValue,
  type
}: {
  nominalDeclineValueDecimal: number;
  bValue: number;
  type: string;
}) {
  let result: number;
  if (type === "Tangent") {
    result = arpsWasm.nominalToTangent(nominalDeclineValueDecimal) * 100;
  } else if (type === "Secant") {
    result = arpsWasm.nominalToSecant(nominalDeclineValueDecimal, bValue) * 100;
  } else {
    result = nominalDeclineValueDecimal * 100;
  }
  // Rounding to six decimal places to ensure values like .999999 are rounded up.
  return Math.round(result * 1000000) / 1000000;
}

export function convertDeclineToNominal({
  declineValuePercent,
  bValue,
  type
}: {
  declineValuePercent: number;
  bValue: number;
  type: string;
}) {
  if (type === "Tangent") {
    return arpsWasm.tangentToNominal(declineValuePercent / 100.0);
  } else if (type === "Secant") {
    return arpsWasm.secantToNominal(declineValuePercent / 100.0, bValue);
  } else {
    return declineValuePercent / 100;
  }
}

export function getRampUpSegmentCumVolume(seg: ArpSegment) {
  return arpsWasm.getSegmentVolume(
    seg.qi,
    seg.di,
    seg.b,
    seg.qf,
    BigInt(Math.floor(new Date(seg.startDate).getTime() / 1000)),
    BigInt(Math.floor(new Date(seg.endDate).getTime() / 1000))
  );
}

export const isRampUpSegment = ({
  segmentTemplateName,
  segments
}: {
  segmentTemplateName: string;
  segments: ArpSegment[];
}): boolean => {
  // Check by segment structure: first segment has qi < qf (rate increasing) and di < 0
  const hasRampUpByStructure =
    segments.length > 2 && segments[0].qi <= segments[0].qf && segments[0].di < 0;

  if (!segmentTemplateName) {
    return hasRampUpByStructure;
  }

  // SAGD with 3 segments includes a ramp-up
  if (
    segmentTemplateName.toLowerCase() === "sagd" &&
    segments.length === 3 &&
    hasRampUpByStructure
  ) {
    return true;
  }

  return segments.length > 2 && segmentTemplateName.toLowerCase().includes("ramp-up");
};

export const isConstrainedSegment = ({
  segmentTemplateName,
  segments
}: {
  segmentTemplateName: string;
  segments: ArpSegment[];
}): boolean => {
  return segments.length === 4 && !isRampUpSegment({ segmentTemplateName, segments });
};

/**
 * Checks if a segment is a flat segment (constant rate, no decline).
 * A flat segment has b=0, di≈0, and qi approximately equals qf.
 */
export const isFlatSegment = (segment: ArpSegment): boolean => {
  const isFlat =
    segment.b === 0 &&
    Math.abs(segment.di) < EPSILON &&
    Math.abs(segment.qi - segment.qf) < EPSILON;
  return isFlat;
};

/**
 * Checks if the segments represent a SAGD template structure.
 * SAGD has either:
 * - 2 segments: flat + exponential decline (b=0)
 * - 3 segments: ramp-up + flat + exponential decline (b=0)
 */
export const isSAGDTemplate = ({
  segmentTemplateName,
  segments
}: {
  segmentTemplateName: string;
  segments: ArpSegment[];
}): boolean => {
  if (segmentTemplateName?.toLowerCase().includes("sagd")) {
    return true;
  }

  if (segments.length === 2) {
    // 2-segment SAGD: flat + decline (both b=0)
    const [seg1, seg2] = segments;
    const seg1IsFlat = isFlatSegment(seg1);
    const seg2IsDecline = seg2.b === 0 && seg2.di > 0;
    return seg1IsFlat && seg2IsDecline;
  }

  if (segments.length === 3) {
    // 3-segment SAGD: ramp-up + flat + decline
    const [seg1, seg2, seg3] = segments;
    const hasRampUp = seg1.qi < seg1.qf && seg1.di < 0;
    const seg2IsFlat = isFlatSegment(seg2);
    const seg3IsDecline = seg3.b === 0 && seg3.di > 0;
    return hasRampUp && seg2IsFlat && seg3IsDecline;
  }

  return false;
};

export interface TypeWellTemplateDisplayData {
  "Start Date": string;
  "End Date": string;
  Q0: number;
  Bcons?: number;
  tCons?: number;
  Qi: number;
  maxRate: number;
  Di?: number;
  B?: number;
  Btrans?: number;
  Bhyp?: number;
  Bf?: number;
  tRamp?: number;
  tTrans?: number;
  tFlat?: number;
  Df?: number;
  Qf: number | string;
  [ArpFields.EUR]: number | string;
  [ArpFields.RestrictedEUR]?: string;
  [ArpFields.QfAtCutoff]?: string;
  // Dynamic decline type fields (e.g., "Di (Tangent)", "Df (Secant)")
  [key: string]: Primitive;
}

export function getTypeWellTemplateDisplayFieldsAndData(
  segmentTemplateName: string,
  productSegments: ArpSegment[],
  declineType: string,
  defaultTypeWell?: ITypeWellProductSettings,
  isPrimaryProduct?: boolean,
  primaryProductEndDate?: Date | null
): TypeWellTemplateDisplayData & { wasCutOff?: boolean; productName?: string } {
  const segmentLength = productSegments.length;
  let eur = 0;

  const isRampUp = isRampUpSegment({
    segments: productSegments,
    segmentTemplateName
  });

  const hasConstrainedSegment = isConstrainedSegment({
    segments: productSegments,
    segmentTemplateName
  });

  const isSAGD = isSAGDTemplate({
    segments: productSegments,
    segmentTemplateName
  });

  const hasInitialSegment = isRampUp || hasConstrainedSegment;

  // Track if we encounter an unsolvable segment - any segments after it should be skipped
  let hasEncounteredUnsolvableSegment = false;

  productSegments.forEach((s) => {
    // If we've already encountered an unsolvable segment, skip all remaining segments
    if (hasEncounteredUnsolvableSegment) {
      return;
    }

    const startDate_unixTimestamp_seconds = Math.round(
      new Date(s.startDate).getTime() / 1000
    );
    const endDate_unixTimestamp_seconds = Math.round(
      new Date(s.endDate).getTime() / 1000
    );
    const segVolume =
      arpsWasm.getSegmentVolume(
        s.qi,
        s.di,
        s.b,
        s.qf,
        BigInt(startDate_unixTimestamp_seconds),
        BigInt(endDate_unixTimestamp_seconds)
      ) * 0.001;

    // If this segment has negative volume, mark it as unsolvable and skip it and all subsequent segments
    if (segVolume < 0) {
      hasEncounteredUnsolvableSegment = true;
      return;
    }

    // Only add valid segment volumes
    eur += segVolume;
  });

  const seg1 = productSegments[0];
  const seg2 = hasInitialSegment ? productSegments[1] : null;

  // Find the last non-constrained segment for accurate End Date
  let lastValidSegmentIndex = segmentLength - 1;

  // Walk backwards to find the last segment that isn't constrained
  for (let i = segmentLength - 1; i >= 0; i--) {
    const segment = productSegments[i];
    const qiEqualsQf = Math.abs(segment.qi - segment.qf) < EPSILON;
    const qiLessThanQf = segment.qi < segment.qf;

    // Check if this segment's Qf matches a later segment's Qf (indicating constraint)
    const hasLaterSegmentWithSameQf = productSegments
      .slice(i + 1)
      .some(
        (laterSeg) =>
          laterSeg.product === segment.product &&
          Math.abs(laterSeg.qf - segment.qf) < EPSILON
      );

    const isConstrained =
      (qiEqualsQf && segment.di > 0) || (qiLessThanQf && hasLaterSegmentWithSameQf);

    if (!isConstrained) {
      lastValidSegmentIndex = i;
      break;
    }
  }

  const lastSegment = productSegments[lastValidSegmentIndex];
  const secondToLastSegment =
    segmentLength > 2 ? productSegments[segmentLength - 2] : null;

  // tRamp calculation:
  // - For SAGD: calculate from ramp segment (3-seg) or return 0 (2-seg, to show empty input)
  // - For ramp-up/constrained templates: calculate from initial segment
  // - Otherwise: undefined (field not shown)
  let tRamp: number | undefined;
  if (isSAGD) {
    // SAGD: 3-segment has ramp in seg1, 2-segment has no ramp
    const sagdHasRamp = segmentLength === 3;
    tRamp = sagdHasRamp ? roundTo(avgMonthsDiff(seg1.startDate, seg1.endDate), 1) : 0;
  } else if (hasInitialSegment) {
    tRamp = roundTo(avgMonthsDiff(seg1.startDate, seg1.endDate), 1);
  }

  const is3SegmentsTemplate = (segmentLength === 3 && !isRampUp) || segmentLength === 4;

  const Bhyp = is3SegmentsTemplate
    ? secondToLastSegment.b
    : segmentLength > 1
    ? hasInitialSegment
      ? seg2.b
      : seg1.b
    : seg1.b;

  const Btrans = is3SegmentsTemplate
    ? hasInitialSegment
      ? seg2.b
      : seg1.b
    : defaultTypeWell?.values.Btrans; // The Previous segment didn't have Btrans, use the default

  const Bf = segmentLength > 1 ? lastSegment?.b ?? seg2.b : defaultTypeWell?.values.Bf; // The Previous segment didn't have Bf, use the default

  // tTrans only available for 3 segment templates
  const tTrans = is3SegmentsTemplate
    ? hasInitialSegment
      ? Math.round(getDurationInMonths(new Date(seg2.startDate), new Date(seg2.endDate)))
      : Math.round(getDurationInMonths(new Date(seg1.startDate), new Date(seg1.endDate)))
    : undefined;

  let baseDiDecimal: number;
  let baseDfDecimal: number;
  let baseB: number;

  if (isSAGD) {
    // SAGD: decline segment is always last
    // get Di and Df from the decline segment, not the flat segment
    const declineSegment = productSegments[segmentLength - 1];
    baseDiDecimal = declineSegment.di;
    baseDfDecimal = declineSegment.df;
    baseB = declineSegment.b;
  } else {
    baseDiDecimal = hasInitialSegment ? seg2.di : seg1.di;
    baseDfDecimal = secondToLastSegment ? secondToLastSegment.df : seg1.df;
    baseB = hasInitialSegment ? seg2.b : seg1.b;
  }

  // tFlat only available for SAGD templates
  const flatSegment = isSAGD
    ? segmentLength === 2
      ? productSegments[0] // 2-segment SAGD: first segment is flat
      : productSegments[1] // 3-segment SAGD: second segment is flat
    : null;
  const tFlat = flatSegment
    ? roundTo(avgMonthsDiff(flatSegment.startDate, flatSegment.endDate), 1)
    : undefined;

  // Calculate Restricted EUR using the utility function
  const { isEndDateLater, restrictedEUR, qfAtCutoff, productName } =
    calculateRestrictedEUR(productSegments, isPrimaryProduct, primaryProductEndDate);

  // For SAGD, Qi comes from the flat segment (which is either seg1 for 2-seg or seg2 for 3-seg)
  const QiValue = isSAGD
    ? flatSegment?.qi ?? seg1.qi
    : hasInitialSegment
    ? seg2.qi
    : seg1.qi;

  // For SAGD: Q0 only applies to 3-segment mode (ramp + flat + decline)
  // For 2-segment SAGD (flat + decline), Q0 should be 0
  const Q0Value = isSAGD
    ? hasInitialSegment
      ? seg1.q0 ?? seg1.qi // 3-segment SAGD: Q0 from ramp segment
      : 0 // 2-segment SAGD: no Q0
    : seg1.q0 ?? seg1.qi;

  if (!declineType) {
    const result = {
      "Start Date": seg1.startDate,
      "End Date": lastSegment.endDate,
      Q0: Q0Value,
      Bcons: hasConstrainedSegment ? seg1.b : undefined,
      tCons: hasConstrainedSegment ? tRamp : undefined,
      Qi: QiValue,
      maxRate: hasInitialSegment ? seg2.maxRate : seg1.maxRate,
      Di: baseDiDecimal,
      B: baseB,
      Btrans: Btrans,
      Bhyp: Bhyp,
      Bf: Bf,
      tRamp: isSAGD || hasInitialSegment ? tRamp : undefined,
      tFlat: tFlat,
      tTrans: tTrans,
      Df: baseDfDecimal,
      Qf: lastSegment.qf,
      [ArpFields.EUR]: eur,
      // Always include these fields (undefined when not applicable, which displays as "N/A")
      [ArpFields.RestrictedEUR]:
        isPrimaryProduct === false && isEndDateLater
          ? restrictedEUR.toFixed(2)
          : undefined,
      [ArpFields.QfAtCutoff]:
        isPrimaryProduct === false && isEndDateLater ? qfAtCutoff.toFixed(2) : undefined,
      wasCutOff: isEndDateLater && isPrimaryProduct === false,
      productName
    };

    return result;
  }

  const result = {
    "Start Date": seg1.startDate,
    "End Date": lastSegment.endDate,
    Q0: Q0Value,
    Bcons: hasConstrainedSegment ? seg1.b : undefined,
    tCons: hasConstrainedSegment ? tRamp : undefined,
    Qi: QiValue,
    maxRate: hasInitialSegment ? seg2.maxRate : seg1.maxRate,
    [getDeclineTitleWithType("Di", declineType)]: roundTo6Decimal(
      convertNominalToDeclineType({
        nominalDeclineValueDecimal: baseDiDecimal,
        bValue: baseB,
        type: declineType
      })
    ),
    B: segmentLength === 1 ? seg1.b : undefined, // B only available for 1 segment templates
    Btrans: is3SegmentsTemplate ? Btrans : undefined, // Btrans only available for 3 segment templates
    Bhyp: segmentLength > 1 ? Bhyp : undefined, // Bhyp only available for 2 segment templates and above
    Bf: segmentLength > 1 ? Bf : undefined, // Bf only available for 2 segment templates and above
    tRamp: isSAGD || hasInitialSegment ? tRamp : undefined,
    tFlat: tFlat,
    tTrans: tTrans,
    [getDeclineTitleWithType("Df", declineType)]: roundTo6Decimal(
      convertNominalToDeclineType({
        nominalDeclineValueDecimal: baseDfDecimal,
        bValue: secondToLastSegment ? secondToLastSegment.b : seg1.b,
        type: declineType
      })
    ),
    Qf: lastSegment.qf.toFixed(2),
    [ArpFields.EUR]: eur.toFixed(2),
    // Always include these fields (undefined when not applicable, which displays as "N/A")
    [ArpFields.RestrictedEUR]:
      isPrimaryProduct === false && isEndDateLater ? restrictedEUR.toFixed(2) : undefined,
    [ArpFields.QfAtCutoff]:
      isPrimaryProduct === false && isEndDateLater ? qfAtCutoff.toFixed(2) : undefined,
    wasCutOff: isEndDateLater && isPrimaryProduct === false,
    productName
  };

  return result;
}

/// The `getDiAtDayFromQiToQf` function calculates the value of di based on the given parameters.
/// The days parameter is the number of days that it will take to decline from qi to qf.
export function getDiAtDayFromQiToQf(
  qi: number,
  qf: number,
  b: number,
  day: number
): number {
  const t = AVG_DAYS_IN_YEAR / day;
  if (Math.abs(b) < EPSILON) {
    return -Math.log(qf / qi) * t;
  } else if (Math.abs(b - 1.0) < EPSILON) {
    return ((qi - qf) / qf) * t;
  } else {
    return ((Math.pow(qf / qi, -b) - 1.0) / b) * t;
  }
}

const roundTo6Decimal = (val: number) => {
  return Math.round(val * 1000000) / 1000000;
};

export function getPrimaryProductLastSegmentEndDate({
  segments,
  kind,
  cumulativeData
}: {
  segments: ArpSegment[];
  kind: ArpsDesignerKind;
  cumulativeData?: CumulativeData;
}): Date | undefined {
  if (segments == undefined) {
    return undefined;
  }
  if (kind === "Forecasting" && cumulativeData?.primaryProduct) {
    const primarySegments = segments.filter(
      (seg) => seg.product === cumulativeData.primaryProduct
    );
    const lastSegment = primarySegments[primarySegments.length - 1];
    if (lastSegment && lastSegment.endDate) {
      return new Date(Date.parse(lastSegment.endDate));
    }
  }

  // Fallback for TypeWell: assume primary product is the first product added
  const primaryProduct: string = segments[0]?.product;
  const primarySegments: ArpSegment[] = segments.filter(
    (seg: ArpSegment) => seg.product === primaryProduct
  );
  const lastPrimarySegment = primarySegments[primarySegments.length - 1];

  if (lastPrimarySegment?.endDate) {
    return new Date(lastPrimarySegment.endDate);
  }
  return undefined;
}

/**
 * Get the end date of the last valid segment for the primary product,
 * excluding segments that have errors or warnings.
 *
 * @param segments - All segments
 * @param kind - ArpsDesignerKind (Forecasting or TypeWell)
 * @param cumulativeData - Cumulative data containing primary product info
 * @param segmentsWithIssues - Set of segment numbers (1-based) that have errors or warnings
 * @returns Date of the last valid segment's end date, or undefined if none found
 */
export function getPrimaryProductLastValidSegmentEndDate({
  segments,
  kind,
  cumulativeData,
  segmentsWithIssues
}: {
  segments: ArpSegment[];
  kind: ArpsDesignerKind;
  cumulativeData?: CumulativeData;
  segmentsWithIssues?: Set<number>;
}): Date | undefined {
  if (segments == undefined) {
    return undefined;
  }

  if (kind === "Forecasting" && cumulativeData?.primaryProduct) {
    const primarySegments = segments.filter(
      (seg) => seg.product === cumulativeData.primaryProduct
    );

    // Find the last valid segment (without errors/warnings)
    let lastValidSegment: ArpSegment | undefined;
    for (let i = primarySegments.length - 1; i >= 0; i--) {
      const segmentNumber = i + 1; // Segment numbers are 1-based
      if (!segmentsWithIssues || !segmentsWithIssues.has(segmentNumber)) {
        lastValidSegment = primarySegments[i];
        break;
      }
    }

    if (lastValidSegment && lastValidSegment.endDate) {
      return new Date(Date.parse(lastValidSegment.endDate));
    }
  }

  // Fallback for TypeWell: assume primary product is the first product added
  const primaryProduct: string = segments[0]?.product;
  const primarySegments: ArpSegment[] = segments.filter(
    (seg: ArpSegment) => seg.product === primaryProduct
  );

  // Find the last valid segment (without errors/warnings)
  let lastValidSegment: ArpSegment | undefined;
  for (let i = primarySegments.length - 1; i >= 0; i--) {
    const segmentNumber = i + 1; // Segment numbers are 1-based
    if (!segmentsWithIssues || !segmentsWithIssues.has(segmentNumber)) {
      lastValidSegment = primarySegments[i];
      break;
    }
  }

  if (lastValidSegment?.endDate) {
    return new Date(lastValidSegment.endDate);
  }
  return undefined;
}

export function getHeaderUnits(
  header: string,
  product: ProductTypeEnum,
  mode?: TypeWellInputTableMode
) {
  if (header.startsWith("Q") || header === "maxRate") {
    return getDefaultRateUnits(product);
  } else if (
    header === ArpFields.EUR ||
    header === "CTD" ||
    header === "TotalEUR" ||
    header === ArpFields.RestrictedEUR
  ) {
    return getDefaultCumUnits(product, true);
  } else if (header === "N2 Switch") {
    return DurationUnit.AverageMonth;
  } else if (header === "Duration") {
    // Duration is in months for ratio products in Product mode, years for ALL products in Segment mode
    if (mode === TypeWellInputTableMode.Segment) {
      return DurationUnit.Year;
    }
    return isRatio(product.toString()) ? DurationUnit.Months : DurationUnit.Year;
  } else if (header.startsWith("Di") || header.startsWith("Df")) {
    return getDefaultDeclineUnits(product);
  } else if (header.startsWith("t")) {
    return DurationUnit.AverageMonth;
  }

  return "";
}

/**
 * Reorders the arps segments array to make a new product the primary product.
 * The primary product is determined by being first in the segments array.
 *
 * @param arps - The current forecast with all segments
 * @param newPrimaryProduct - The product name to make primary
 * @returns Updated forecast with reordered segments
 */
export function reorderArpsForNewPrimaryProduct(
  arps: ICheckedForecast,
  newPrimaryProduct: string
): ICheckedForecast {
  if (!arps?.arps || arps.arps.length === 0) {
    return arps;
  }

  const currentFirstProduct = arps.arps[0]?.product;
  if (currentFirstProduct === newPrimaryProduct) {
    return arps;
  }

  // Separate segments by product while maintaining segment order
  const segmentsByProduct = new Map<string, ArpSegment[]>();

  arps.arps.forEach((segment) => {
    const product = segment.product;
    if (!segmentsByProduct.has(product)) {
      segmentsByProduct.set(product, []);
    }
    const productSegments = segmentsByProduct.get(product);
    if (productSegments) {
      productSegments.push(segment);
    }
  });

  if (!segmentsByProduct.has(newPrimaryProduct)) {
    return arps;
  }

  // Reorder: new primary first, then others in original order
  const reorderedSegments: ArpSegment[] = [];
  const newPrimarySegments = segmentsByProduct.get(newPrimaryProduct);
  if (newPrimarySegments) {
    reorderedSegments.push(...newPrimarySegments);
  }

  // Maintain original order of other products
  const productOrder = [...new Set(arps.arps.map((seg) => seg.product))];
  const sortedOtherProducts = productOrder.filter(
    (product) => product !== newPrimaryProduct
  );

  sortedOtherProducts.forEach((product) => {
    const segments = segmentsByProduct.get(product);
    if (segments) {
      reorderedSegments.push(...segments);
    }
  });

  return {
    ...arps,
    arps: reorderedSegments
  };
}
